export type CrmPlanId = 'crm_small' | 'crm_medium' | 'crm_large' | 'crm_enterprise';

export type CrmPlanCurrency = 'USD' | 'TZS';

/** Fixed USD ↔ TZS rate (matches unified plan pricing and server checkout). */
export const CRM_USD_TO_TZS_RATE = 2500;

export interface CrmPlanOption {
  id: CrmPlanId;
  planId: CrmPlanId;
  name: string;
  price: string;
  priceUsd: number;
  seatLimit: number | null;
  description: string;
  features: string[];
}

export const CRM_PLAN_FALLBACK: CrmPlanOption[] = [
  {
    id: 'crm_small',
    planId: 'crm_small',
    name: 'Small Business Package',
    price: '$50',
    priceUsd: 50,
    seatLimit: 2,
    description: 'Up to 2 sales team members',
    features: ['2 sales team invites', 'Lead pipeline', 'Platinum digital card included', 'Campaign tools'],
  },
  {
    id: 'crm_medium',
    planId: 'crm_medium',
    name: 'Medium Business Package',
    price: '$100',
    priceUsd: 100,
    seatLimit: 4,
    description: 'Up to 4 sales team members',
    features: ['4 sales team invites', 'Lead pipeline', 'Platinum digital card included', 'Reports & analytics'],
  },
  {
    id: 'crm_large',
    planId: 'crm_large',
    name: 'Large Business Package',
    price: '$250',
    priceUsd: 250,
    seatLimit: 10,
    description: 'Up to 10 sales team members',
    features: ['10 sales team invites', 'Lead pipeline', 'Platinum digital card included', 'GPS location tracking', 'Sales route tracking (rolling out)'],
  },
  {
    id: 'crm_enterprise',
    planId: 'crm_enterprise',
    name: 'Enterprise Package',
    price: '$1500',
    priceUsd: 1500,
    seatLimit: null,
    description: 'Unlimited sales team per business branch — $1,500/month per branch.',
    features: ['Unlimited sales team members per branch', '$1,500/month per business branch', 'Lead pipeline & tracking', 'Platinum digital card included', 'Priority support', 'Full analytics suite'],
  },
];

export function usdToTzs(usd: number): number {
  return Math.round(Number(usd) * CRM_USD_TO_TZS_RATE);
}

export function isCrmPlanId(planId: string | null | undefined): planId is CrmPlanId {
  return CRM_PLAN_FALLBACK.some((p) => p.planId === String(planId || '').trim().toLowerCase());
}

export function resolveCrmPlanKey(raw: string | null | undefined): CrmPlanId | null {
  const n = String(raw || '').trim().toLowerCase();
  if (!n) return null;
  if (isCrmPlanId(n)) return n;
  if (n.includes('enterprise')) return 'crm_enterprise';
  if (n.includes('large') && n.includes('business')) return 'crm_large';
  if (n.includes('medium') && n.includes('business')) return 'crm_medium';
  if (n.includes('small') && n.includes('business')) return 'crm_small';
  return null;
}

export function getCrmPlanById(planId: string): CrmPlanOption | null {
  const key = resolveCrmPlanKey(planId);
  if (!key) return null;
  return CRM_PLAN_FALLBACK.find((p) => p.planId === key) || null;
}

export function getCrmPlanMonthlyPrice(plan: CrmPlanOption | CrmPlanId, currency: CrmPlanCurrency): number {
  const option = typeof plan === 'string' ? getCrmPlanById(plan) : plan;
  if (!option) return 0;
  return currency === 'TZS' ? usdToTzs(option.priceUsd) : option.priceUsd;
}

export function getCrmPlanPriceByMonths(plan: CrmPlanOption | CrmPlanId, months: number, currency: CrmPlanCurrency): number {
  const numMonths = Math.max(1, Math.round(Number(months) || 1));
  return getCrmPlanMonthlyPrice(plan, currency) * numMonths;
}
